import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import {
  listOrganizerDirectory,
  type DirectoryListParams,
  type OrganizerDirectoryEntry,
  type ProspectOrgType,
} from "@/api/event-organizers";

// Friendly labels for the org-type enum.
export const ORG_TYPE_LABEL: Record<ProspectOrgType, string> = {
  regional_linguistic: "Regional / Linguistic",
  pan_indian_cultural: "Pan-Indian Cultural",
  cultural: "Cultural / Arts",
  educational: "Educational",
  community_center: "Community Center",
  student_alumni: "Student / Alumni",
};

const METROS = [
  "SF Bay Area",
  "Dallas-Fort Worth",
  "Houston",
  "LA/OC",
  "Edison/NY-NJ",
];

export default function OrganizerDirectory() {
  const [filters, setFilters] = useState<DirectoryListParams>({ limit: 24 });
  const [query, setQuery] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["organizer-directory", filters, query],
    queryFn: () => listOrganizerDirectory({ ...filters, search: query || undefined }),
  });

  const items = data?.data?.items ?? [];
  const meta = data?.data?.meta;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organizer Directory"
        subtitle="Researched cultural organizers not yet on WeDeshi. Find yours and claim it — an admin reviews, then your page goes live pre-filled."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Event Organizers", href: "/lab/event-organizers" },
          { label: "Directory" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <InputField
            label="Search"
            placeholder="Org name, acronym, or city"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <SelectField
            label="Metro"
            value={filters.metro ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, metro: e.target.value || undefined })
            }
            options={[
              { value: "", label: "All metros" },
              ...METROS.map((m) => ({ value: m, label: m })),
            ]}
          />
          <SelectField
            label="Type"
            value={filters.orgType ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                orgType: (e.target.value || undefined) as DirectoryListParams["orgType"],
              })
            }
            options={[
              { value: "", label: "All types" },
              ...(Object.keys(ORG_TYPE_LABEL) as ProspectOrgType[]).map((k) => ({
                value: k,
                label: ORG_TYPE_LABEL[k],
              })),
            ]}
          />
          <InputField
            label="State"
            placeholder="CA, TX, NJ…"
            value={filters.state ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, state: e.target.value || undefined })
            }
          />
        </div>
      </section>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
          Failed to load the directory.{" "}
          <button onClick={() => refetch()} className="underline font-medium">
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">
          No organizers match these filters. Claimed organizers drop out of the
          directory — try widening the search.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((o) => (
              <DirectoryCard key={o.id} o={o} />
            ))}
          </div>
          {meta && (
            <p className="text-xs text-gray-400">
              Showing {items.length} of {meta.total} un-claimed organizers
            </p>
          )}
        </>
      )}
    </div>
  );
}

function DirectoryCard({ o }: { o: OrganizerDirectoryEntry }) {
  return (
    <Link
      to={`/lab/event-organizers/directory/${o.id}`}
      className="block bg-white border border-gray-200 rounded-2xl p-4 hover:border-primary-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center text-xl shrink-0">
          🎪
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{o.orgName}</h3>
            {o.acronymAliases[0] && (
              <span className="text-xs text-gray-400 shrink-0">
                {o.acronymAliases[0]}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {[o.city, o.state].filter(Boolean).join(", ")}
            {o.metroArea ? ` · ${o.metroArea}` : ""}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {o.orgType && (
              <Badge variant="gray">{ORG_TYPE_LABEL[o.orgType]}</Badge>
            )}
            {o.regionOrLanguage && (
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600">
                {o.regionOrLanguage}
              </span>
            )}
            {o.foundedYear && (
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600">
                est. {o.foundedYear}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
